import React, { useRef } from 'react';

// Default helpers if not passed via props
const defaultGetRecallImages = (round) => {
  if (!round) return [];
  const list = [];
  if (round.recallImageUrl) {
    list.push(round.recallImageUrl);
  }
  if (Array.isArray(round.recallImageUrls)) {
    round.recallImageUrls.forEach((url) => {
      if (url && !list.includes(url)) {
        list.push(url);
      }
    });
  }
  return list;
};

// eslint-disable-next-line no-unused-vars
const defaultHighlightActionText = (text) => {
  if (!text) return '';
  const parts = text.split(/(รหัส\s*[A-Za-z0-9-]+|โดย\s*[ก-๙a-zA-Z0-9\s]+|รอบที่\s*\d+|เครื่องนึ่ง\s*[ก-๙a-zA-Z0-9-\s]+)/g);
  return parts.map((part, index) => {
    if (part.startsWith('รหัส')) {
      return <span key={index} style={{ color: '#2563eb', fontWeight: 'bold' }}>{part}</span>;
    }
    if (part.startsWith('โดย')) {
      return <span key={index} style={{ color: '#059669', fontWeight: 'bold' }}>{part}</span>;
    }
    if (part.startsWith('รอบที่') || part.startsWith('เครื่องนึ่ง')) {
      return <span key={index} style={{ color: '#d97706', fontWeight: 'bold' }}>{part}</span>;
    }
    return part;
  });
};

const RecallPage = ({
  filterRecallItemName = '',
  setFilterRecallItemName,
  filterRecallBarcode = '',
  setFilterRecallBarcode,
  filterRecallMfgDate = '',
  setFilterRecallMfgDate,
  filterRecallPreparedBy = '',
  setFilterRecallPreparedBy,
  getFilteredPreparers,
  filterRecallCheckedBy = '',
  setFilterRecallCheckedBy,
  getFilteredCheckers,
  filterRecallMachine = '',
  setFilterRecallMachine,
  machinesList = [],
  filterRecallCycle = '',
  setFilterRecallCycle,
  cyclesList = [],
  sterileOptions,
  handleRecallSearch,
  isRecallSearching = false,
  handleClearRecallFilters,
  recallSearchResults = [],
  selectedRecallIds = [],
  setSelectedRecallIds,
  handleToggleRecallSelectAll,
  handleToggleRecallSelect,
  handleExecuteRecall,
  handleRecallAction,
  recallReason = '',
  setRecallReason,
  activeUserRole = '',
  isAdmin = false,
  setCancelRecallPromptItem,
  handleApproveCancelRecall,
  handleRejectCancelRecall,
  recallCameraInputRef,
  recallGalleryInputRef,
  handleRecallImageChange,
  isRecallUploading = false,
  recallImageUrl = '',
  handleRemoveRecallImage,
  setShowRecallUploadPopup,
  allRecallBatches = [],
  recallRoundsPage = 1,
  setRecallRoundsPage,
  fetchAllRecallBatches,
  isFetchingAllBatches = false,
  getRecallImages: propGetRecallImages,
  setZoomedRecallImageUrl,
  setSelectedDetailCountName,
  setDetailModalItems,
  setActiveRecallBatchIdForUpload,
  handleDeleteRecallRound,
  // eslint-disable-next-line no-unused-vars
  highlightActionText = defaultHighlightActionText,
}) => {
  const localCameraInputRef = useRef(null);
  const localGalleryInputRef = useRef(null);
  const cameraRef = recallCameraInputRef || localCameraInputRef;
  const galleryRef = recallGalleryInputRef || localGalleryInputRef;

  const getRecallImages = propGetRecallImages || defaultGetRecallImages;
  const onRecallAction = handleExecuteRecall || handleRecallAction;
  const userIsAdmin = activeUserRole === 'admin' || isAdmin;

  // Resolve options from props or sterileOptions
  const preparers = typeof getFilteredPreparers === 'function'
    ? getFilteredPreparers()
    : Array.isArray(getFilteredPreparers)
      ? getFilteredPreparers
      : (sterileOptions?.preparers || []);

  const checkers = typeof getFilteredCheckers === 'function'
    ? getFilteredCheckers()
    : Array.isArray(getFilteredCheckers)
      ? getFilteredCheckers
      : (sterileOptions?.checkers || []);

  const machines = (machinesList && machinesList.length > 0)
    ? machinesList
    : (sterileOptions?.machines || []);

  const cycles = (cyclesList && cyclesList.length > 0)
    ? cyclesList
    : (sterileOptions?.cycles || []);

  return (
    <div style={{ background: '#fff', padding: '30px', borderRadius: '16px', boxShadow: '0 8px 30px rgba(0,0,0,0.05)', border: '1px solid #eef2f6', marginBottom: '30px' }}>
      <div style={{ textAlign: 'center', marginBottom: '30px' }}>
        <span style={{ fontSize: '3.5em', display: 'block', marginBottom: '10px' }}>⚠️</span>
        <h2 style={{ color: '#c62828', margin: '0 0 5px 0', fontSize: '1.8em', fontWeight: 'bold' }}>เรียกคืนวัสดุและเครื่องมือที่มีปัญหา (Material Recall)</h2>
        <p style={{ color: '#7f8c8d', margin: 0, fontSize: '0.95em' }}>อายัดและเรียกคืนวัสดุในระบบที่ผ่านกระบวนการทำลายเชื้อ (Sterile) จากเครื่องนึ่งหรือรอบนึ่งที่มีปัญหา เพื่อส่งกลับห้องล้างเครื่องมือเท่านั้น</p>
      </div>

      {/* Warning visual alert card */}
      <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: '10px', padding: '15px 20px', marginBottom: '25px', display: 'flex', gap: '15px', alignItems: 'center' }}>
        <span style={{ fontSize: '1.8em' }}>💡</span>
        <div style={{ fontSize: '0.9em', color: '#5d4037', lineHeight: '1.5' }}>
          <strong>คำแนะนำการใช้งาน:</strong> เลือกกรองรายการเครื่องมือโดยระบุชื่อวัสดุ บาร์โค้ด วันที่ผลิต ผู้เตรียม/ผู้ตรวจ หรือระบุเครื่องนึ่งและรอบนึ่งที่มีปัญหา
          ระบบจะค้นหาเครื่องมือที่ <strong>กำลังทำงานอยู่ (Active)</strong> และตรงกับเงื่อนไข จากนั้นกดปุ่ม <strong>"อายัดวัสดุทั้งหมดที่กรอง"</strong> ด้านล่าง
          เพื่อเปลี่ยนสถานะเป็นถูกอายัด (Recalled) ซึ่งจะป้องกันไม่ให้นำวัสดุเหล่านี้ไปใช้งานในห้อง Sterile หรือห้องจ่ายเครื่องมือ
        </div>
      </div>

      {/* Filter controls grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '15px', marginBottom: '20px' }}>

        {/* Item Name filter */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <label style={{ fontWeight: 'bold', color: '#34495e', fontSize: '0.9em' }}>ชื่อวัสดุ</label>
          <input
            type="text"
            placeholder="ค้นหาชื่อวัสดุ..."
            value={filterRecallItemName}
            onChange={(e) => setFilterRecallItemName && setFilterRecallItemName(e.target.value)}
            style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95em' }}
          />
        </div>

        {/* Barcode filter */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <label style={{ fontWeight: 'bold', color: '#34495e', fontSize: '0.9em' }}>บาร์โค้ด</label>
          <input
            type="text"
            placeholder="รหัสบาร์โค้ด..."
            value={filterRecallBarcode}
            onChange={(e) => setFilterRecallBarcode && setFilterRecallBarcode(e.target.value)}
            style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95em' }}
          />
        </div>

        {/* Mfg Date filter */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <label style={{ fontWeight: 'bold', color: '#34495e', fontSize: '0.9em' }}>วันที่ผลิต (MFG Date)</label>
          <input
            type="date"
            value={filterRecallMfgDate}
            onChange={(e) => setFilterRecallMfgDate && setFilterRecallMfgDate(e.target.value)}
            style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95em' }}
          />
        </div>

        {/* Preparer filter */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <label style={{ fontWeight: 'bold', color: '#34495e', fontSize: '0.9em' }}>ผู้เตรียม</label>
          <select
            value={filterRecallPreparedBy}
            onChange={(e) => setFilterRecallPreparedBy && setFilterRecallPreparedBy(e.target.value)}
            style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95em' }}
          >
            <option value="">-- ทั้งหมด --</option>
            {preparers.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        </div>

        {/* Checker filter */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <label style={{ fontWeight: 'bold', color: '#34495e', fontSize: '0.9em' }}>ผู้ตรวจ</label>
          <select
            value={filterRecallCheckedBy}
            onChange={(e) => setFilterRecallCheckedBy && setFilterRecallCheckedBy(e.target.value)}
            style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95em' }}
          >
            <option value="">-- ทั้งหมด --</option>
            {checkers.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        </div>

        {/* Sterile Machine filter */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <label style={{ fontWeight: 'bold', color: '#34495e', fontSize: '0.9em' }}>เครื่องนึ่ง</label>
          <select
            value={filterRecallMachine}
            onChange={(e) => setFilterRecallMachine && setFilterRecallMachine(e.target.value)}
            style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95em' }}
          >
            <option value="">-- ทั้งหมด --</option>
            {machines.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        </div>

        {/* Sterile Cycle filter */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <label style={{ fontWeight: 'bold', color: '#34495e', fontSize: '0.9em' }}>รอบนึ่ง</label>
          <select
            value={filterRecallCycle}
            onChange={(e) => setFilterRecallCycle && setFilterRecallCycle(e.target.value)}
            style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95em' }}
          >
            <option value="">-- ทั้งหมด --</option>
            {cycles.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        </div>

      </div>

      {/* Filter buttons */}
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginBottom: '25px', borderBottom: '1px solid #e2e8f0', paddingBottom: '20px' }}>
        <button
          onClick={handleClearRecallFilters}
          style={{
            background: '#f1f5f9',
            color: '#475569',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '8px',
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'background 0.2s'
          }}
          onMouseOver={(e) => { e.target.style.background = '#e2e8f0'; }}
          onMouseOut={(e) => { e.target.style.background = '#f1f5f9'; }}
        >
          ล้างตัวกรอง
        </button>
        <button
          onClick={handleRecallSearch}
          disabled={isRecallSearching}
          style={{
            background: '#2c3e50',
            color: '#fff',
            border: 'none',
            padding: '10px 24px',
            borderRadius: '8px',
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'background 0.2s',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          onMouseOver={(e) => { e.target.style.background = '#1a252f'; }}
          onMouseOut={(e) => { e.target.style.background = '#2c3e50'; }}
        >
          {isRecallSearching ? '⏳ กำลังค้นหา...' : '🔍 ค้นหาเครื่องมือที่มีปัญหา'}
        </button>
      </div>

      {/* Results Table Section */}
      <div>
        <h3 style={{ color: '#2c3e50', marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>📋 รายการเครื่องมือที่ตรงตามเงื่อนไข</span>
          <span style={{ fontSize: '0.85em', background: '#e0f2fe', color: '#0369a1', padding: '4px 10px', borderRadius: '12px', fontWeight: 'bold' }}>
            พบ {recallSearchResults.length} รายการ
          </span>
        </h3>

        {recallSearchResults.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8', background: '#f8fafc', borderRadius: '12px', border: '1px dashed #cbd5e1' }}>
            <span style={{ fontSize: '2em', display: 'block', marginBottom: '10px' }}>🔍</span>
            ไม่พบรายการวัสดุที่กำลังใช้งานที่ตรงตามตัวกรอง กรุณาปรับเปลี่ยนเงื่อนไขการค้นหา
          </div>
        ) : (
          <>
            <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', marginBottom: '20px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.9em', color: '#1e293b' }}>
                <thead style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr>
                    <th style={{ padding: '12px', width: '50px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={
                          recallSearchResults.filter((i) => i.status !== 'recalled').length > 0 &&
                          selectedRecallIds.length === recallSearchResults.filter((i) => i.status !== 'recalled').length
                        }
                        onChange={(e) => {
                          if (typeof handleToggleRecallSelectAll === 'function') {
                            handleToggleRecallSelectAll(e.target.checked);
                            return;
                          }
                          if (e.target.checked) {
                            if (setSelectedRecallIds) {
                              setSelectedRecallIds(recallSearchResults.filter((i) => i.status !== 'recalled').map((i) => i.id));
                            }
                          } else {
                            if (setSelectedRecallIds) {
                              setSelectedRecallIds([]);
                            }
                          }
                        }}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                    </th>
                    <th style={{ padding: '12px', color: '#475569', fontWeight: 'bold' }}>รหัสบาร์โค้ด</th>
                    <th style={{ padding: '12px', color: '#475569', fontWeight: 'bold' }}>ชื่อวัสดุ</th>
                    <th style={{ padding: '12px', color: '#475569', fontWeight: 'bold' }}>เครื่องนึ่ง / รอบ</th>
                    <th style={{ padding: '12px', color: '#475569', fontWeight: 'bold' }}>ผู้เตรียม / ผู้ตรวจ</th>
                    <th style={{ padding: '12px', color: '#475569', fontWeight: 'bold' }}>วันที่ผลิต</th>
                    <th style={{ padding: '12px', color: '#475569', fontWeight: 'bold' }}>สถานะปัจจุบัน</th>
                    <th style={{ padding: '12px', color: '#475569', fontWeight: 'bold' }}>การจัดการอายัด</th>
                  </tr>
                </thead>
                <tbody>
                  {recallSearchResults.map((item) => {
                    const details = item.details || {};
                    const statusColor = item.status === 'recalled' ? '#ef4444' : item.status === 'sterile-ready' ? '#10b981' : '#f59e0b';
                    const statusBg = item.status === 'recalled' ? '#fef2f2' : item.status === 'sterile-ready' ? '#ecfdf5' : '#fffbeb';

                    return (
                      <tr key={item.id} style={{ borderBottom: '1px solid #f1f5f9', background: item.status === 'recalled' ? '#fff5f5' : '#fff' }}>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            disabled={item.status === 'recalled'}
                            checked={selectedRecallIds.includes(item.id)}
                            onChange={(e) => {
                              if (typeof handleToggleRecallSelect === 'function') {
                                handleToggleRecallSelect(item.id, e.target.checked);
                                return;
                              }
                              if (setSelectedRecallIds) {
                                if (e.target.checked) {
                                  setSelectedRecallIds((prev) => [...prev, item.id]);
                                } else {
                                  setSelectedRecallIds((prev) => prev.filter((id) => id !== item.id));
                                }
                              }
                            }}
                            style={{ width: '18px', height: '18px', cursor: item.status === 'recalled' ? 'not-allowed' : 'pointer' }}
                          />
                        </td>
                        <td style={{ padding: '12px', fontWeight: 'bold', color: '#1e293b' }}>{item.unique_barcode}</td>
                        <td style={{ padding: '12px' }}>{item.item_name}</td>
                        <td style={{ padding: '12px' }}>
                          ⚙️ {details.machine || '-'} / รอบ {details.cycle || '-'}
                        </td>
                        <td style={{ padding: '12px' }}>
                          👤 {details.preparedBy || '-'} / {details.checkedBy || '-'}
                        </td>
                        <td style={{ padding: '12px' }}>{details.mfgDate || '-'}</td>
                        <td style={{ padding: '12px' }}>
                          <span style={{ display: 'inline-block', padding: '4px 8px', borderRadius: '6px', fontSize: '0.85em', fontWeight: 'bold', color: statusColor, background: statusBg }}>
                            {item.status === 'recalled' ? '⚠️ ถูกอายัด (Recall)' : item.status === 'sterile-ready' ? '✅ sterile-ready' : item.status}
                          </span>
                        </td>
                        <td style={{ padding: '12px' }}>
                          {item.status === 'recalled' ? (
                            (() => {
                              const request = details.cancelRecallRequest;
                              if (!request) {
                                return (
                                  <button
                                    onClick={() => setCancelRecallPromptItem && setCancelRecallPromptItem({ item, reason: '' })}
                                    style={{
                                      background: '#3498db',
                                      color: '#fff',
                                      border: 'none',
                                      padding: '6px 12px',
                                      borderRadius: '6px',
                                      fontSize: '0.82em',
                                      fontWeight: 'bold',
                                      cursor: 'pointer',
                                      transition: 'background 0.2s'
                                    }}
                                    onMouseOver={(e) => { e.target.style.background = '#2980b9'; }}
                                    onMouseOut={(e) => { e.target.style.background = '#3498db'; }}
                                  >
                                    🔓 ขอยกเลิกอายัด
                                  </button>
                                );
                              } else if (request.status === 'pending') {
                                if (userIsAdmin) {
                                  return (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                      <span style={{ fontSize: '0.8em', color: '#dc2626', fontWeight: 'bold' }}>
                                        ⏳ รออนุมัติยกเลิกอายัด
                                      </span>
                                      <span style={{ fontSize: '0.78em', color: '#64748b', fontStyle: 'italic', wordBreak: 'break-all', maxWidth: '180px' }}>
                                        เหตุผล: {request.reason}
                                      </span>
                                      <div style={{ display: 'flex', gap: '5px' }}>
                                        <button
                                          onClick={() => handleApproveCancelRecall && handleApproveCancelRecall(item)}
                                          style={{
                                            background: '#2ecc71',
                                            color: '#fff',
                                            border: 'none',
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            fontSize: '0.78em',
                                            fontWeight: 'bold',
                                            cursor: 'pointer'
                                          }}
                                        >
                                          ✅ อนุมัติ
                                        </button>
                                        <button
                                          onClick={() => handleRejectCancelRecall && handleRejectCancelRecall(item)}
                                          style={{
                                            background: '#e74c3c',
                                            color: '#fff',
                                            border: 'none',
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            fontSize: '0.78em',
                                            fontWeight: 'bold',
                                            cursor: 'pointer'
                                          }}
                                        >
                                          ❌ ปฏิเสธ
                                        </button>
                                      </div>
                                    </div>
                                  );
                                } else {
                                  return (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      <span style={{ display: 'inline-flex', padding: '4px 8px', borderRadius: '4px', fontSize: '0.8em', color: '#d35400', background: '#fff3e0', fontWeight: 'bold' }}>
                                        ⏳ รอ Admin อนุมัติ
                                      </span>
                                      <span style={{ fontSize: '0.78em', color: '#64748b', fontStyle: 'italic', wordBreak: 'break-all', maxWidth: '180px' }}>
                                        ({request.reason})
                                      </span>
                                    </div>
                                  );
                                }
                              }
                              return '-';
                            })()
                          ) : (
                            <span style={{ color: '#94a3b8', fontSize: '0.85em' }}>-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Recall Action block at the bottom */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '12px', padding: '20px', marginTop: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px' }}>
                <div style={{ flex: 1, minWidth: '280px' }}>
                  <h4 style={{ color: '#991b1b', margin: '0 0 5px 0', fontSize: '1em', fontWeight: 'bold' }}>⚠️ ดำเนินการอายัดวัสดุกลุ่มนี้</h4>
                  <p style={{ color: '#7f1d1d', margin: 0, fontSize: '0.85em', lineHeight: '1.4' }}>
                    เมื่อกดปุ่มยืนยัน เครื่องมือที่เลือกจำนวน <strong>{selectedRecallIds.length} จาก {recallSearchResults.length} รายการ</strong> ในตารางด้านบน จะถูกเปลี่ยนสถานะเป็น <strong>"ถูกอายัด (Recall)"</strong> ทันที
                    และระบบจะบล็อกห้ามทำการเบิก/จ่าย/อบซ้ำในห้องจ่ายและห้อง Sterile จนกว่าจะทำการล้างและคืนเข้าห้องล้างเครื่องมือเรียบร้อยแล้ว
                  </p>
                </div>
                <button
                  onClick={onRecallAction}
                  style={{
                    background: '#dc2626',
                    color: '#fff',
                    border: 'none',
                    padding: '14px 28px',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    fontSize: '1em',
                    cursor: 'pointer',
                    boxShadow: '0 4px 6px rgba(220, 38, 38, 0.2)',
                    transition: 'background 0.2s',
                    whiteSpace: 'nowrap',
                    alignSelf: 'center'
                  }}
                  onMouseOver={(e) => { e.target.style.background = '#b91c1c'; }}
                  onMouseOut={(e) => { e.target.style.background = '#dc2626'; }}
                >
                  ⚠️ อายัดวัสดุที่เลือกไว้ (ส่งคืนห้องล้าง)
                </button>
              </div>

              {/* Reason input field */}
              <div style={{ borderTop: '1px solid #fca5a5', paddingTop: '15px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontWeight: 'bold', color: '#991b1b', fontSize: '0.90em', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  📝 ระบุเหตุผลการอายัด / เรียกคืนวัสดุ <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="ระบุเหตุผล เช่น เครื่องนึ่งมีความร้อนไม่ถึงเกณฑ์, ตรวจพบสิ่งสกปรกในซอง, ซองฉีกขาด..."
                  value={recallReason}
                  onChange={(e) => setRecallReason && setRecallReason(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid #fca5a5',
                    fontSize: '0.95em',
                    background: '#fff',
                    color: '#1e293b',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Image upload field */}
              <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontWeight: 'bold', color: '#991b1b', fontSize: '0.90em' }}>
                  📸 รูปภาพวัสดุที่มีปัญหา (ถ้ามี - ไม่บังคับ)
                </label>

                {/* Hidden file inputs */}
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  ref={cameraRef}
                  style={{ display: 'none' }}
                  onChange={handleRecallImageChange}
                />
                <input
                  type="file"
                  accept="image/*"
                  ref={galleryRef}
                  style={{ display: 'none' }}
                  onChange={handleRecallImageChange}
                />

                {isRecallUploading ? (
                  <div style={{ padding: '10px', fontSize: '0.9em', color: '#b91c1c', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>⏳ กำลังอัปโหลดรูปภาพวัสดุมีปัญหา...</span>
                  </div>
                ) : recallImageUrl ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px', background: '#fef2f2', padding: '10px', borderRadius: '8px', border: '1px dashed #fca5a5', width: 'fit-content' }}>
                    <div style={{ width: '80px', height: '80px', borderRadius: '6px', overflow: 'hidden', border: '1px solid #fee2e2', position: 'relative' }}>
                      <img src={recallImageUrl} alt="รูปวัสดุมีปัญหา" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveRecallImage}
                      style={{
                        background: '#ef4444',
                        color: '#fff',
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.85em',
                        fontWeight: 'bold'
                      }}
                    >
                      ✕ ลบรูปภาพ
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowRecallUploadPopup && setShowRecallUploadPopup(true)}
                    style={{
                      alignSelf: 'flex-start',
                      padding: '8px 16px',
                      background: '#fecdd3',
                      border: '1px solid #fda4af',
                      color: '#991b1b',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.88em',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'background 0.2s'
                    }}
                    onMouseOver={(e) => { e.target.style.background = '#fdbabc'; }}
                    onMouseOut={(e) => { e.target.style.background = '#fecdd3'; }}
                  >
                    📷 แนบรูปถ่ายวัสดุมีปัญหา
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Recall summary table (show all historical recall rounds) */}
      {allRecallBatches.length > 0 ? (() => {
        const roundsPerPage = 3;
        const totalPages = Math.ceil(allRecallBatches.length / roundsPerPage);
        const startIndex = (recallRoundsPage - 1) * roundsPerPage;
        const paginatedRounds = allRecallBatches.slice(startIndex, startIndex + roundsPerPage);

        return (
          <div style={{ marginTop: '30px', background: '#f8fafc', padding: '24px', borderRadius: '14px', border: '1px solid #e2e8f0', textAlign: 'left' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ margin: 0, color: '#1e293b', fontSize: '1.25em', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
                📊 ประวัติและสรุปผลการเรียกคืนวัสดุทุกรอบ ({allRecallBatches.length} รอบ)
              </h3>
              <button
                onClick={fetchAllRecallBatches}
                disabled={isFetchingAllBatches}
                style={{
                  background: '#fff',
                  border: '1px solid #cbd5e1',
                  borderRadius: '8px',
                  padding: '6px 12px',
                  fontSize: '0.85em',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}
              >
                {isFetchingAllBatches ? '⏳ กำลังโหลด...' : '🔄 รีเฟรชข้อมูล'}
              </button>
            </div>
            <p style={{ color: '#64748b', fontSize: '0.88em', marginTop: 0, marginBottom: '20px' }}>
              สรุปสถานะการเรียกคืนเครื่องมือแยกตามรอบ โดยระบบจะอัปเดตสถานะแบบ Real-time เมื่ออุปกรณ์ถูกส่งคืนที่ห้องล้างเครื่องมือ
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {paginatedRounds.map((round) => {
                const tsMatch = round.batchId?.match(/RECALL-(\d+)/);
                const timestamp = tsMatch ? parseInt(tsMatch[1], 10) : 0;
                const dateStr = timestamp ? new Date(timestamp).toLocaleDateString('th-TH', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                }) : 'ไม่ทราบวันที่';
                const timeStr = timestamp ? new Date(timestamp).toLocaleTimeString('th-TH', {
                  hour: '2-digit',
                  minute: '2-digit'
                }) : 'ไม่ทราบเวลา';

                return (
                  <div key={round.batchId} style={{ background: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                    {/* Round Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px', flexWrap: 'wrap', gap: '10px' }}>
                      <div>
                        <span style={{ fontSize: '1.05em', fontWeight: 'bold', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          🔔 รอบเรียกคืน: วันที่ {dateStr} เวลา {timeStr} น.
                        </span>
                        <div style={{ fontSize: '0.82em', color: '#64748b', marginTop: '4px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                          <span>รหัสรอบ: <code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px', fontFamily: 'monospace' }}>{round.batchId}</code></span>
                          {round.machine && <span>• ⚙️ เครื่อง: <strong>{round.machine}</strong></span>}
                          {round.cycle && <span>• 🔄 รอบนึ่ง: <strong>{round.cycle}</strong></span>}
                          {round.mfgDate && <span>• 📅 วันผลิต: <strong>{round.mfgDate}</strong></span>}
                        </div>
                        {round.recallReason && (
                          <div style={{ fontSize: '0.82em', color: '#b91c1c', marginTop: '4px', fontWeight: 'bold' }}>
                            📝 เหตุผลการอายัด: {round.recallReason}
                          </div>
                        )}
                        {getRecallImages(round).length > 0 && (
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                            {getRecallImages(round).map((imgUrl, idx) => (
                              <img
                                key={idx}
                                src={imgUrl}
                                alt={`ภาพถ่ายวัสดุมีปัญหา ${idx + 1}`}
                                style={{
                                  maxWidth: '120px',
                                  maxHeight: '120px',
                                  borderRadius: '6px',
                                  border: '1px solid #fee2e2',
                                  objectFit: 'cover',
                                  cursor: 'pointer',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                }}
                                onClick={() => setZoomedRecallImageUrl && setZoomedRecallImageUrl(imgUrl)}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '0.85em', background: '#fef2f2', color: '#dc2626', padding: '4px 12px', borderRadius: '12px', fontWeight: 'bold', border: '1px solid #fee2e2' }}>
                          ทั้งหมด {round.total} รายการ
                        </span>
                        {handleDeleteRecallRound && (
                          <button
                            type="button"
                            onClick={() => handleDeleteRecallRound(round.batchId || round.id)}
                            style={{
                              background: '#fee2e2',
                              color: '#dc2626',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '4px 8px',
                              fontSize: '0.8em',
                              cursor: 'pointer',
                              fontWeight: 'bold'
                            }}
                            title="ลบรอบเรียกคืน"
                          >
                            🗑️ ลบ
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Metrics Grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '15px' }}>
                      {/* Total recalled card */}
                      <div style={{ background: '#f8fafc', padding: '15px 10px', borderRadius: '10px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                        <div style={{ fontSize: '0.82em', color: '#64748b', fontWeight: 'bold', marginBottom: '6px' }}>จำนวนที่เรียกคืนทั้งหมด</div>
                        <div style={{ fontSize: '1.6em', fontWeight: 'bold', color: '#2c3e50', marginBottom: '10px' }}>{round.total}</div>
                        <button
                          onClick={() => {
                            if (setSelectedDetailCountName) setSelectedDetailCountName(`จำนวนเรียกคืนทั้งหมด (รอบ ${dateStr} ${timeStr} น.)`);
                            if (setDetailModalItems) setDetailModalItems(round.items);
                          }}
                          style={{ background: '#f1f5f9', border: 'none', color: '#1e293b', width: '100%', padding: '6px', borderRadius: '6px', fontSize: '0.8em', fontWeight: 'bold', cursor: 'pointer', transition: 'background 0.2s' }}
                          onMouseOver={(e) => { e.target.style.background = '#e2e8f0'; }}
                          onMouseOut={(e) => { e.target.style.background = '#f1f5f9'; }}
                        >
                          🔍 ดูรายละเอียด
                        </button>
                      </div>

                      {/* Pending recall card */}
                      <div style={{ background: '#fffbeb', padding: '15px 10px', borderRadius: '10px', border: '1px solid #fef3c7', textAlign: 'center' }}>
                        <div style={{ fontSize: '0.82em', color: '#b45309', fontWeight: 'bold', marginBottom: '6px' }}>คงเหลือในระบบ (ที่ต้องเก็บคืน)</div>
                        <div style={{ fontSize: '1.6em', fontWeight: 'bold', color: '#d97706', marginBottom: '10px' }}>{round.pending}</div>
                        <button
                          onClick={() => {
                            if (setSelectedDetailCountName) setSelectedDetailCountName(`คงเหลือในระบบที่ต้องเก็บคืน (รอบ ${dateStr} ${timeStr} น.)`);
                            if (setDetailModalItems) setDetailModalItems(round.items?.filter((i) => i.status === 'recalled' && i.is_active));
                          }}
                          style={{ background: '#fff9db', border: 'none', color: '#b45309', width: '100%', padding: '6px', borderRadius: '6px', fontSize: '0.8em', fontWeight: 'bold', cursor: 'pointer', transition: 'background 0.2s' }}
                          onMouseOver={(e) => { e.target.style.background = '#fef08a'; }}
                          onMouseOut={(e) => { e.target.style.background = '#fff9db'; }}
                        >
                          🔍 ดูรายละเอียด
                        </button>
                      </div>

                      {/* Used (retrieved too late) card */}
                      <div style={{ background: '#fef2f2', padding: '15px 10px', borderRadius: '10px', border: '1px solid #fee2e2', textAlign: 'center' }}>
                        <div style={{ fontSize: '0.82em', color: '#b91c1c', fontWeight: 'bold', marginBottom: '6px' }}>ถูกใช้งานไปแล้ว (เก็บไม่ทัน)</div>
                        <div style={{ fontSize: '1.6em', fontWeight: 'bold', color: '#dc2626', marginBottom: '10px' }}>{round.used}</div>
                        <button
                          onClick={() => {
                            if (setSelectedDetailCountName) setSelectedDetailCountName(`ถูกใช้งานไปแล้ว/เก็บไม่ทัน (รอบ ${dateStr} ${timeStr} น.)`);
                            if (setDetailModalItems) setDetailModalItems(round.items?.filter((i) => i.details?.recallOutcome === 'used'));
                          }}
                          style={{ background: '#fde8e8', border: 'none', color: '#b91c1c', width: '100%', padding: '6px', borderRadius: '6px', fontSize: '0.8em', fontWeight: 'bold', cursor: 'pointer', transition: 'background 0.2s' }}
                          onMouseOver={(e) => { e.target.style.background = '#fbd5d5'; }}
                          onMouseOut={(e) => { e.target.style.background = '#fde8e8'; }}
                        >
                          🔍 ดูรายละเอียด
                        </button>
                      </div>

                      {/* Collected cleanly card */}
                      <div style={{ background: '#ecfdf5', padding: '15px 10px', borderRadius: '10px', border: '1px solid #d1fae5', textAlign: 'center' }}>
                        <div style={{ fontSize: '0.82em', color: '#047857', fontWeight: 'bold', marginBottom: '6px' }}>ถูกเก็บคืนสำเร็จ</div>
                        <div style={{ fontSize: '1.6em', fontWeight: 'bold', color: '#10b981', marginBottom: '10px' }}>{round.collected}</div>
                        <button
                          onClick={() => {
                            if (setSelectedDetailCountName) setSelectedDetailCountName(`ถูกเก็บคืนสำเร็จ/ไม่ได้ใช้งาน (รอบ ${dateStr} ${timeStr} น.)`);
                            if (setDetailModalItems) setDetailModalItems(round.items?.filter((i) => i.details?.recallOutcome === 'not_used'));
                          }}
                          style={{ background: '#d1fae5', border: 'none', color: '#047857', width: '100%', padding: '6px', borderRadius: '6px', fontSize: '0.8em', fontWeight: 'bold', cursor: 'pointer', transition: 'background 0.2s' }}
                          onMouseOver={(e) => { e.target.style.background = '#a7f3d0'; }}
                          onMouseOut={(e) => { e.target.style.background = '#d1fae5'; }}
                        >
                          🔍 ดูรายละเอียด
                        </button>
                      </div>

                      {/* Problem photo card */}
                      {getRecallImages(round).length > 0 ? (
                        <div style={{ background: '#fff1f2', padding: '15px 10px', borderRadius: '10px', border: '1px solid #ffe4e6', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'center', textAlign: 'center', minHeight: '130px' }}>
                          <div style={{ fontSize: '0.82em', color: '#be123c', fontWeight: 'bold', marginBottom: '6px' }}>รูปภาพปัญหา ({getRecallImages(round).length} รูป)</div>

                          {/* Thumbnails list */}
                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '8px', maxHeight: '42px', overflowY: 'auto' }}>
                            {getRecallImages(round).map((imgUrl, idx) => (
                              <div
                                key={idx}
                                style={{ width: '32px', height: '32px', borderRadius: '4px', overflow: 'hidden', border: '1px solid #fda4af', cursor: 'pointer' }}
                                onClick={() => setZoomedRecallImageUrl && setZoomedRecallImageUrl(imgUrl)}
                              >
                                <img src={imgUrl} alt={`Problem ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              </div>
                            ))}
                          </div>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
                            <button
                              onClick={() => {
                                if (setZoomedRecallImageUrl) setZoomedRecallImageUrl(getRecallImages(round)[0]);
                              }}
                              style={{ background: '#ffe4e6', border: 'none', color: '#be123c', width: '100%', padding: '5px', borderRadius: '6px', fontSize: '0.78em', fontWeight: 'bold', cursor: 'pointer', transition: 'background 0.2s' }}
                              onMouseOver={(e) => { e.target.style.background = '#fecdd3'; }}
                              onMouseOut={(e) => { e.target.style.background = '#ffe4e6'; }}
                            >
                              🖼️ เปิดดูรูปภาพ
                            </button>

                            <button
                              onClick={() => {
                                if (setActiveRecallBatchIdForUpload) setActiveRecallBatchIdForUpload(round.batchId);
                                if (setShowRecallUploadPopup) setShowRecallUploadPopup(true);
                              }}
                              style={{ background: '#dc2626', border: 'none', color: '#fff', width: '100%', padding: '5px', borderRadius: '6px', fontSize: '0.78em', fontWeight: 'bold', cursor: 'pointer', transition: 'background 0.2s' }}
                              onMouseOver={(e) => { e.target.style.background = '#b91c1c'; }}
                              onMouseOut={(e) => { e.target.style.background = '#dc2626'; }}
                            >
                              ➕ เพิ่มรูปภาพ
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ background: '#f8fafc', padding: '15px 10px', borderRadius: '10px', border: '1px dashed #cbd5e1', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', minHeight: '130px', gap: '8px' }}>
                          <div style={{ fontSize: '0.82em', color: '#94a3b8', fontWeight: 'bold' }}>ไม่มีรูปภาพแนบ</div>
                          <button
                            onClick={() => {
                              if (setActiveRecallBatchIdForUpload) setActiveRecallBatchIdForUpload(round.batchId);
                              if (setShowRecallUploadPopup) setShowRecallUploadPopup(true);
                            }}
                            style={{ background: '#cbd5e1', border: 'none', color: '#475569', padding: '6px 12px', borderRadius: '6px', fontSize: '0.78em', fontWeight: 'bold', cursor: 'pointer', transition: 'background 0.2s' }}
                            onMouseOver={(e) => { e.target.style.background = '#94a3b8'; }}
                            onMouseOut={(e) => { e.target.style.background = '#cbd5e1'; }}
                          >
                            ➕ เพิ่มรูปภาพ
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '24px' }}>
                <button
                  disabled={recallRoundsPage === 1}
                  onClick={() => setRecallRoundsPage && setRecallRoundsPage((prev) => Math.max(prev - 1, 1))}
                  style={{
                    background: '#fff',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    fontSize: '0.85em',
                    fontWeight: 'bold',
                    color: recallRoundsPage === 1 ? '#94a3b8' : '#475569',
                    cursor: recallRoundsPage === 1 ? 'not-allowed' : 'pointer',
                    transition: 'background 0.2s'
                  }}
                >
                  ◀ ย้อนกลับ
                </button>

                {Array.from({ length: totalPages }).map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setRecallRoundsPage && setRecallRoundsPage(idx + 1)}
                    style={{
                      background: recallRoundsPage === idx + 1 ? '#2c3e50' : '#fff',
                      color: recallRoundsPage === idx + 1 ? '#fff' : '#2c3e50',
                      border: '1px solid #cbd5e1',
                      borderRadius: '8px',
                      padding: '8px 14px',
                      fontSize: '0.85em',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                  >
                    {idx + 1}
                  </button>
                ))}

                <button
                  disabled={recallRoundsPage === totalPages}
                  onClick={() => setRecallRoundsPage && setRecallRoundsPage((prev) => Math.min(prev + 1, totalPages))}
                  style={{
                    background: '#fff',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    fontSize: '0.85em',
                    fontWeight: 'bold',
                    color: recallRoundsPage === totalPages ? '#94a3b8' : '#475569',
                    cursor: recallRoundsPage === totalPages ? 'not-allowed' : 'pointer',
                    transition: 'background 0.2s'
                  }}
                >
                  ถัดไป ▶
                </button>
              </div>
            )}
          </div>
        );
      })() : (
        <div style={{ marginTop: '30px', background: '#f8fafc', padding: '30px', borderRadius: '14px', border: '1px dashed #cbd5e1', textAlign: 'center', color: '#94a3b8' }}>
          <span style={{ fontSize: '2.5em', display: 'block', marginBottom: '10px' }}>📊</span>
          ยังไม่มีประวัติการเรียกคืนวัสดุในระบบ
        </div>
      )}
    </div>
  );
};

export default RecallPage;
